import { yupResolver } from '@hookform/resolvers/yup'
import { DateOnly } from '@if/core/date'
import { EFinancingProgram } from '@if/core/types'
import { Button, Grid, Stack, Typography } from '@mui/material'
import { PageError, PageSpinner } from '@src/components'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import {
  useComputeMechanicalWarrantyLoan,
  useUpdateMechanicalWarrantyWorksheet,
} from '@src/data/api/mechanical-warranty-worksheet/mechanical-warranty-worksheet-api'
import {
  useGetMerchantProviderByType,
  useGetMerchantProviderProducts,
} from '@src/data/api/merchant/merchant-api'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { reportErrorToConsole } from '@src/services/error-logger'
import { isValidFirstPayment } from '@src/services/paymentDate-utils'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MechanicalWarrantyComputeRequestDto } from '../types/ComputeMechanicalWarrantyRequestDto'
import { MechanicalWarrantyComputeResultDto } from '../types/ComputeMechanicalWarrantyResultDto'
import {
  buildMechanicalWarrantyWorksheetSchema,
  MechanicalWarrantyWorksheet,
  MechanicalWarrantyWorksheetDto,
} from '../types/mechanicalWarrantyWorksheetSchema'
import MechanicalWarrantyExtended from './components/MechanicalWarrantyExtended'
import MechanicalWarrantyWorksheetDetails from './components/MechanicalWarrantyWorksheetDetails'
import MechanicalWarrantyWorksheetLoanPaymentDetails from './components/MechanicalWarrantyWorksheetLoanPaymentDetails'

import { useFirstPaymentDateOptions } from '../../editWorksheet-hooks'

const EditMechanicalWarrantyWorksheetPage = () => {
  // Get data from context instead of Redux
  const { creditApplication, worksheet: contextWorksheet, isLoading: isLoadingCreditApplication } = useCreditApplicationData()
  const worksheet = contextWorksheet as MechanicalWarrantyWorksheet | undefined

  // Local state for computed info (no longer in Redux)
  const [computedInfo, setComputedInfo] = useState<MechanicalWarrantyComputeResultDto>({} as MechanicalWarrantyComputeResultDto)

  const defaultValues = worksheet ?? buildMechanicalWarrantyWorksheetSchema(creditApplication)

  const { setValue, getValues, watch, handleSubmit, control, reset } = useForm<MechanicalWarrantyWorksheetDto>({
    mode: 'onBlur',
    defaultValues: defaultValues as MechanicalWarrantyWorksheetDto,
    resolver: yupResolver(buildMechanicalWarrantyWorksheetSchema(creditApplication)),
  })

  const navigate = useNavigate()
  const { t } = useTranslation()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [updateWorksheet] = useUpdateMechanicalWarrantyWorksheet()
  const [computeFunding, isComputingMechanicalWarrantyLoan] = useComputeMechanicalWarrantyLoan()

  const [
    paymentFrequency,
    term,
    firstPaymentOn,
    deliveryOn,
    options,
    surcharges,
    providerId,
    mechanicalWarrantyProductId,
  ] = watch([
    'paymentFrequency',
    'term',
    'firstPaymentOn',
    'deliveryOn',
    'additions.options',
    'additions.surcharges',
    'providerId',
    'mechanicalWarrantyProductId',
  ])
  // computedInfo now from local state instead of Redux
  const error = useBusinessErrors()
  // Fetch providers with React-Query (no Redux syncing needed)
  const [mechanicalWarrantyProviders, isLoadingProviders] = useGetMerchantProviderByType(
    creditApplication?.merchantId ?? '',
    EFinancingProgram.MechanicalWarranty as unknown as number,
  )

  // Fetch products with React-Query (no Redux syncing needed)
  const [mechanicalProducts] = useGetMerchantProviderProducts(
    creditApplication?.merchantId ?? '',
    EFinancingProgram.MechanicalWarranty as unknown as number,
    providerId ?? '',
  )

  // Reset form when worksheet data arrives
  useEffect(() => {
    if (worksheet && mechanicalWarrantyProviders.length > 0) {
      reset(worksheet as MechanicalWarrantyWorksheetDto)
    }
  }, [worksheet, mechanicalWarrantyProviders, reset])

  const doComputeFunding = useCallback(
    async (computeDto: MechanicalWarrantyComputeRequestDto) => {
      try {
        const data = await computeFunding(computeDto)
        setComputedInfo(data)
      } catch (error) {
        reportErrorToConsole(error)
      }
    },
    [computeFunding],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedComputeFunding = useCallback(debounce(doComputeFunding, 500), [doComputeFunding])

  useEffect(() => {
    if (
      [paymentFrequency, term, deliveryOn, firstPaymentOn, providerId, mechanicalWarrantyProductId].every((x) => !!x) &&
      isValidFirstPayment(deliveryOn, firstPaymentOn)
    ) {
      const calculationData = {
        creditApplicationId: creditApplication!.id,
        providerId: providerId,
        paymentFrequency: paymentFrequency,
        term,
        firstPaymentDate: firstPaymentOn,
        deliveryOn: deliveryOn,
        options: options,
        surcharges: surcharges,
        mechanicalWarrantyProductId: mechanicalWarrantyProductId,
      } as MechanicalWarrantyComputeRequestDto

      try {
        void debouncedComputeFunding(calculationData)
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }, [
    debouncedComputeFunding,
    paymentFrequency,
    term,
    firstPaymentOn,
    deliveryOn,
    options,
    surcharges,
    creditApplication,
    providerId,
    mechanicalWarrantyProductId,
  ])

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn: new DateOnly(deliveryOn),
    paymentFrequency,
    isHighEstimate: false,
    setValue,
    getValues,
  })

  const onSubmit = async (data: MechanicalWarrantyWorksheetDto) => {
    try {
      await updateWorksheet(data)
      await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
      navigate(`/Applications/${EFinancingProgram.MechanicalWarranty}/${creditApplication!.id}/view`, {
        replace: true,
      })
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={isLoadingCreditApplication || isLoadingProviders} />
      <Stack>
        <PageError errors={error} />
      </Stack>
      {creditApplication && mechanicalWarrantyProviders.length > 0 && (
        <form onSubmit={handleSubmit(onSubmit, reportErrorToConsole)}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack direction="row" spacing={2}>
                <Typography flexGrow={1} variant="h4">
                  {t('mechanicalWarrantyWorksheet.title')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth={false} variant="contained" onClick={() => navigate(-1)}>
                    {t('common.previous')}
                  </Button>
                  <Button
                    fullWidth={false}
                    variant="contained"
                    type="submit"
                    disabled={isComputingMechanicalWarrantyLoan || !computedInfo}
                  >
                    {t('common.save')}
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={4}>
                <MechanicalWarrantyExtended
                  mechanicalWarrantyProviders={mechanicalWarrantyProviders}
                  control={control}
                  setValue={setValue}
                  mechanicalWarrantyProducts={mechanicalProducts}
                />
              </Grid>
              <Grid size={8}>
                <Stack direction="column" spacing={2} sx={{ height: '100%' }}>
                  <MechanicalWarrantyWorksheetDetails
                    creditApplication={creditApplication}
                    computedLoanAmounts={computedInfo}
                    isComputing={isComputingMechanicalWarrantyLoan}
                    control={control}
                    setValue={setValue}
                    mechanicalWarrantyProducts={mechanicalProducts}
                    minFirstPaymentDate={minFirstPaymentDate}
                    maxFirstPaymentDate={maxFirstPaymentDate}
                  />
                  <MechanicalWarrantyWorksheetLoanPaymentDetails
                    computedLoanAmounts={computedInfo}
                    isComputing={isComputingMechanicalWarrantyLoan}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </div>
  )
}

export default EditMechanicalWarrantyWorksheetPage
